import React, { useState, useEffect } from "react";
import { Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import CustomCkeditor from "../../../common/customeditor";
import * as consentServices from "../../../services/consent.services";
import { SWAL_SETTINGS, showFilterList, INPUT_LENGTH_50 } from "../../../../../utils/Constants";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../../utils/commonfunction";
import { Tooltip, Whisper } from "rsuite";
import { useTranslation } from "react-i18next";

const ConsentAdd = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [initialValues, setInitialValues] = useState({
    title: "",
    version: "",
    type: "",
    status: "ACTIVE",
    description: "",
  });

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: "User Consent Management", url: "/admin/cms/consent/list/1" },
    { title: t("link_add"), url: "" },
  ];

  useEffect(() => {
    setInitialValues({
      title: state && state?.title ? state?.title : "",
      version: state && state?.version ? state?.version : "",
      type: state && state?.type ? state?.type : "",
      status: state && state?.status ? state.status : "ACTIVE",
      description: state && state?.description ? state?.description : "",
    });
    setDataLoaded(true);
  }, [state]);

  const typeOptions = [
    { value: "PARENT_LINK_CONSENT", label: "Parent Link Consent" },
    { value: "PARENT_LINK_CONSENT_TEEN", label: "Parent Link Consent Teen" },
    { value: "SELF_CONSENT_17", label: "Self Consent 17" },

    { value: "CLUB_ADMIN_CONSENT", label: "Club Admin Consent" },
    { value: "PARENT_SELF_CONSENT", label: "Parent Self Consent" },
    { value: "PRIVACY_POLICY", label: "Privacy Policy" },
    { value: "TERMS_CONDITIONS", label: "Terms Conditions" },
    { value: "DATA_PROTECTION", label: "Data Protection" },
    { value: "SAFE_GUARD_POLICY", label: "Safe Guard Policy" }
  ];

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      {dataLoaded ? (
        <Formik
          initialValues={initialValues}
          validateOnBlur={false}
          validate={(values) => {
            const error = {};
            const title = values.title?.trim() || "";
            const version = values.version?.trim() || "";
            const description = values.description;

            if (!title) error.title = "Title is required";
            else if (title.length > INPUT_LENGTH_50)
              error.title = t("validation_err_reached_maximum_length");

            if (!version) error.version = "Version is required";
            else if (version.length > 20)
              error.version = "Version should be less than 20 characters";

            if (!values.type) error.type = "Consent type is required";

            if (!values.status) error.status = t("validation_err_status_required");

            if (!description) error.description = "Consent description is required";

            return error;
          }}

          onSubmit={(values, { setSubmitting, resetForm }) => {
            setSubmitted(true);
            globalLoader(true);
            const payload = {
              title: values.title,
              version: values.version,
              type: values.type,
              status: values.status,
              description: values.description,
            };

            consentServices
              .Add(payload)
              .then((response) => {              
                setSubmitting(false);
                if (response.success) {
                  Swal.fire({
                    icon: "success",
                    text: response.message || "Consent created successfully",
                    ...SWAL_SETTINGS,
                  });
                  setTimeout(() => {
                    resetForm({ values: "" });
                    globalLoader(false);
                    navigate(`/admin/cms/consent/list/1`);
                  }, 1000);
                } else {
                  Swal.fire({
                    icon: "error",
                    text: handleServerValidations(response),
                    ...SWAL_SETTINGS,
                  });
                  setSubmitted(false);
                  globalLoader(false);
                }
              })
              .catch((error) => {
                console.log("error ====> ", error);
                setSubmitted(false);
                globalLoader(false);
              });
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="row row-sm">
                <div className="col-lg-12 col-md-12 animation_fade">
                  <div className="card custom-card">
                    <div className="card-body">
                      <div>
                        <h6 className="main-content-label mb-3">Add User Consent Configuration</h6>
                      </div>
                      <div className="row row-sm">
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="title" className="text-left d-flex">
                            Consent Title:<span className="requirestar">*</span>
                            <Whisper
                              placement="top"
                              controlId="control-id-hover"
                              trigger="hover"
                              speaker={
                                <Tooltip>
                                  Enter a descriptive name for this user consent
                                </Tooltip>
                              }
                            >
                              <span className="field-more-info mt-1 ms-1 cp">
                                ?
                              </span>
                            </Whisper>
                          </label>
                          <input
                            name="title"
                            id="title"
                            type="text"
                            placeholder="Enter the title"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.title}
                            className="form-control"
                          />
                          <span className="text-danger d-flex text-left">
                            {errors.title && touched.title && errors.title}
                          </span>
                        </div>
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="version" className="text-left d-flex">
                            Version (e.g. v1, v1.1):<span className="requirestar">*</span>
                          </label>
                          <input
                            name="version"
                            id="version"
                            type="text"
                            placeholder="Enter the version number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.version}
                            className="form-control"
                          />
                          <span className="text-danger d-flex text-left">
                            {errors.version && touched.version && errors.version}
                          </span>
                        </div>
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="type" className="text-left d-flex">
                            Consent Type:<span className="requirestar">*</span>
                          </label>
                          <div className="select-down-arrow">
                            <select
                              name="type"
                              id="type"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.type}
                              className="form-control"
                            >
                              <option value="">Select Consent Type</option>
                              {typeOptions.map((opt, i) => (
                                <option key={i} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <span className="text-danger d-flex text-left">
                            {errors.type && touched.type && errors.type}
                          </span>
                        </div>
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="status" className="text-left d-flex">
                            {t("list_heading_status")}:<span className="requirestar">*</span>
                          </label>
                          <div className="select-down-arrow">
                            <select
                              name="status"
                              id="status"
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.status}
                              className="form-control"
                            >
                              {showFilterList.map((value, i) => (
                                <option key={i} value={value.status__id}>
                                  {t(`${value.name}`)}
                                </option>
                              ))}
                            </select>
                          </div>
                          <span className="text-danger d-flex text-left">
                            {errors.status && touched.status && errors.status}
                          </span>
                        </div>
                        <div className="col-lg-12 text-center form-group">
                          <label htmlFor="description" className="text-left d-flex">
                            Consent Text / Policy Details:<span className="requirestar">*</span>
                          </label>
                          <CustomCkeditor
                            fieldname={"description"}
                            setFieldValue={setFieldValue}
                            value={values.description}
                            setFieldTouched={setFieldTouched}
                          />
                          <span className="text-danger d-flex text-left">
                            {errors.description && touched.description && errors.description}
                          </span>
                        </div>
                        <div className="mt-3">
                          <button
                            className="btn btn-main-primary signbtn mr-2"
                            type="submit"
                            disabled={submitted}
                          >
                            <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                            {t("btn_submit")}
                          </button>
                          <button
                            className="btn ripple btn-secondary"
                            type="button"
                            disabled={submitted}
                            onClick={() => navigate(-1)}
                          >
                            <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                            {t("btn_cancel")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      ) : (
        <></>
      )}
    </>
  );
};

export default ConsentAdd;
